import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Organisation, OrganisationService } from '../../../services/organisation.service';
import {
  HOME_COUNTRY_CODE,
  isEuCountry,
  normalizeVatId,
  resolveCountryCode,
  taxTreatment,
  taxTreatmentKey,
  vatIdError,
} from '../../../utils/countries.util';

/**
 * Legal forms offered in the dropdown.
 *
 * `value` is what lands in `clients.legal_form` and MUST stay byte-stable — the
 * DE entries reproduce the keys the previous `LegalFormsDE` enum stored, so
 * existing rows keep selecting correctly.
 */
export interface LegalFormOption {
  value: string;
  de: string;
  en: string;
  group: 'de' | 'eu';
}

/** Sentinel select value that reveals the free-text field. Never stored —
 * `legal_form` always receives either a LEGAL_FORMS value or the typed text. */
export const LEGAL_FORM_CUSTOM = '__custom__';

export const LEGAL_FORMS: LegalFormOption[] = [
  // --- Germany (values unchanged from the original enum keys) ---
  { value: 'Einzelunternehmen', de: 'Einzelunternehmen', en: 'Sole proprietorship (Einzelunternehmen)', group: 'de' },
  { value: 'GbR', de: 'Gesellschaft bürgerlichen Rechts (GbR)', en: 'Civil law partnership (GbR)', group: 'de' },
  { value: 'OHG', de: 'Offene Handelsgesellschaft (OHG)', en: 'General partnership (OHG)', group: 'de' },
  { value: 'KG', de: 'Kommanditgesellschaft (KG)', en: 'Limited partnership (KG)', group: 'de' },
  { value: 'GmbH', de: 'Gesellschaft mit beschränkter Haftung (GmbH)', en: 'Limited liability company (GmbH)', group: 'de' },
  { value: 'UG', de: 'Unternehmergesellschaft (haftungsbeschränkt) (UG)', en: 'Entrepreneurial company (UG, limited liability)', group: 'de' },
  { value: 'GmbH & Co. KG', de: 'GmbH & Co. KG', en: 'GmbH & Co. KG', group: 'de' },
  { value: 'AG', de: 'Aktiengesellschaft (AG)', en: 'Stock corporation (AG)', group: 'de' },
  { value: 'KGaA', de: 'Kommanditgesellschaft auf Aktien (KGaA)', en: 'Partnership limited by shares (KGaA)', group: 'de' },
  { value: 'eG', de: 'Eingetragene Genossenschaft (eG)', en: 'Registered cooperative (eG)', group: 'de' },
  { value: 'PartG', de: 'Partnerschaftsgesellschaft (PartG)', en: 'Partnership company (PartG)', group: 'de' },
  { value: 'Stiftung', de: 'Stiftung', en: 'Foundation (Stiftung)', group: 'de' },
  { value: 'Verein', de: 'Verein (e.V.)', en: 'Registered association (e.V.)', group: 'de' },
  { value: 'KdöR', de: 'Körperschaft des öffentlichen Rechts (KdöR)', en: 'Public law corporation (KdöR)', group: 'de' },

  // --- Rest of Europe. Entity type names are proper nouns and stay as they
  // are; only the country prefix and the descriptive part are translated.
  { value: 'GmbH (AT)', de: 'Österreich — GmbH', en: 'Austria — GmbH', group: 'eu' },
  { value: 'AG (CH)', de: 'Schweiz — AG', en: 'Switzerland — AG', group: 'eu' },
  { value: 'GmbH (CH)', de: 'Schweiz — GmbH / Sàrl', en: 'Switzerland — GmbH / Sàrl', group: 'eu' },
  { value: 'BV', de: 'Niederlande — Besloten Vennootschap (BV)', en: 'Netherlands — Besloten Vennootschap (BV)', group: 'eu' },
  { value: 'NV', de: 'Niederlande / Belgien — Naamloze Vennootschap (NV)', en: 'Netherlands / Belgium — Naamloze Vennootschap (NV)', group: 'eu' },
  { value: 'SARL', de: 'Frankreich — SARL', en: 'France — SARL', group: 'eu' },
  { value: 'SAS', de: 'Frankreich — SAS / SASU', en: 'France — SAS / SASU', group: 'eu' },
  { value: 'SA', de: 'Frankreich / Belgien / Spanien — SA', en: 'France / Belgium / Spain — SA', group: 'eu' },
  { value: 'SRL', de: 'Italien — SRL', en: 'Italy — SRL', group: 'eu' },
  { value: 'SpA', de: 'Italien — SpA', en: 'Italy — SpA', group: 'eu' },
  { value: 'SL', de: 'Spanien — Sociedad Limitada (SL)', en: 'Spain — Sociedad Limitada (SL)', group: 'eu' },
  { value: 'Lda', de: 'Portugal — Lda', en: 'Portugal — Lda', group: 'eu' },
  { value: 'Sp. z o.o.', de: 'Polen — Sp. z o.o.', en: 'Poland — Sp. z o.o.', group: 'eu' },
  { value: 's.r.o.', de: 'Tschechien / Slowakei — s.r.o.', en: 'Czechia / Slovakia — s.r.o.', group: 'eu' },
  { value: 'Kft.', de: 'Ungarn — Kft.', en: 'Hungary — Kft.', group: 'eu' },
  { value: 'ApS', de: 'Dänemark — ApS', en: 'Denmark — ApS', group: 'eu' },
  { value: 'AB', de: 'Schweden — Aktiebolag (AB)', en: 'Sweden — Aktiebolag (AB)', group: 'eu' },
  { value: 'AS', de: 'Norwegen — AS', en: 'Norway — AS', group: 'eu' },
  { value: 'Oy', de: 'Finnland — Oy', en: 'Finland — Oy', group: 'eu' },
  { value: 'Ltd', de: 'Irland / UK — Limited (Ltd)', en: 'Ireland / UK — Limited (Ltd)', group: 'eu' },
  { value: 'PLC', de: 'UK — Public Limited Company (PLC)', en: 'UK — Public Limited Company (PLC)', group: 'eu' },
  { value: 'SE', de: 'Europäische Gesellschaft (SE)', en: 'European Company (SE)', group: 'eu' },
];

@Component({
  selector: 'app-organisation-general-form',
  templateUrl: './organisation-general-form.component.html',
  styleUrl: './organisation-general-form.component.scss',
})
export class OrganisationGeneralFormComponent implements OnInit {
  form!: FormGroup;
  isEditing = false;

  /** Saved billing country. Reverse charge is gated on the persisted value, not
   * on an unsaved edit in the address card next to this one. */
  private savedCountryCode: string | null = null;

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    protected organisationService: OrganisationService
  ) {
    this.form = this.fb.group({
      company_name: ['', [Validators.required]],
      phone: [''],
      tax_number: [''],
      vat_id: ['', [this.vatIdValidator]],
      legal_form: [''],
      legal_form_custom: [''],
      reverse_charge_requested: [false],
    });
  }

  ngOnInit() {
    this.organisationService.organisation$.subscribe((org) => {
      if (org) {
        this.savedCountryCode =
          org.country_code || resolveCountryCode(org.country) || null;
        this.form.patchValue({
          company_name: org.company_name || '',
          phone: org.phone || '',
          tax_number: org.tax_number || '',
          vat_id: org.vat_id || '',
          reverse_charge_requested: !!org.reverse_charge_requested,
        });
        this.patchLegalForm(org.legal_form || '');
        this.syncReverseChargeState();
      }
    });

    // A VAT ID typed now can make the toggle available without a save.
    this.form.get('vat_id')?.valueChanges.subscribe(() => {
      this.syncReverseChargeState();
    });
  }

  /** A stored value that is not in the catalogue (legacy row, or something the
   * user typed) selects "other" and lands in the free-text field. */
  private patchLegalForm(stored: string) {
    const known = LEGAL_FORMS.some((f) => f.value === stored);
    this.form.patchValue({
      legal_form: stored ? (known ? stored : LEGAL_FORM_CUSTOM) : '',
      legal_form_custom: stored && !known ? stored : '',
    });
  }

  readonly legalFormCustomValue = LEGAL_FORM_CUSTOM;

  get isCustomLegalForm(): boolean {
    return this.form?.get('legal_form')?.value === LEGAL_FORM_CUSTOM;
  }

  private vatIdValidator = (control: AbstractControl) => {
    const key = vatIdError(control.value);
    return key ? { vatId: key } : null;
  };

  /** Reverse charge only applies to an EU business outside DE that supplied a
   * VAT ID. The backend enforces the same rule — this just keeps the UI honest. */
  get reverseChargeAvailable(): boolean {
    const code = this.savedCountryCode;
    const vat = normalizeVatId(this.form?.get('vat_id')?.value);
    return (
      !!code &&
      isEuCountry(code) &&
      code !== HOME_COUNTRY_CODE &&
      !!vat &&
      !this.form?.get('vat_id')?.errors
    );
  }

  /** VAT that applies to the SAVED country and the VAT ID as typed. */
  get taxStatusKey(): string {
    return taxTreatmentKey(
      taxTreatment(this.savedCountryCode, this.form?.get('vat_id')?.value)
    );
  }

  private syncReverseChargeState() {
    const control = this.form.get('reverse_charge_requested');
    if (!control) {
      return;
    }
    if (!this.reverseChargeAvailable && control.value) {
      control.setValue(false, { emitEvent: false });
    }
  }

  get vatIdErrorKey(): string | null {
    const control = this.form.get('vat_id');
    if (!control?.touched || !control?.errors) {
      return null;
    }
    return control.errors['vatId'] || null;
  }

  get legalFormsDE() {
    return LEGAL_FORMS.filter((f) => f.group === 'de');
  }

  get legalFormsEU() {
    return LEGAL_FORMS.filter((f) => f.group === 'eu');
  }

  /** Entity names are proper nouns, so these live in the constant rather than
   * in i18n — only the descriptive half differs per language. */
  legalFormLabel(form: LegalFormOption): string {
    const lang = this.translate.currentLang || this.translate.defaultLang || 'de';
    return lang.startsWith('de') ? form.de : form.en;
  }

  async onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { legal_form_custom, ...values } = this.form.value;

    const payload: Partial<Organisation> = {
      ...values,
      // The sentinel never reaches the DB - store the typed text instead.
      legal_form: this.isCustomLegalForm
        ? (legal_form_custom || '').trim()
        : values.legal_form,
      // Empty string, never undefined: `undefined` is dropped by JSON.stringify,
      // so clearing the field would silently keep the old value server-side.
      vat_id: normalizeVatId(this.form.value.vat_id),
      // Never send a toggle the rules do not allow — the server would reset it
      // anyway and the UI would then disagree with the stored value.
      reverse_charge_requested: this.reverseChargeAvailable
        ? !!this.form.value.reverse_charge_requested
        : false,
    };

    this.organisationService.updateMyOrganisation(payload).subscribe((data) => {
      this.isEditing = false;
    });
  }

  showInputError(name: string) {
    return this.form.get(name)?.touched && this.form.get(name)?.invalid;
  }
}
